import { useQuery } from '@tanstack/react-query';
import { Alert, Descriptions, Divider, Drawer, Input, Spin, Typography } from 'antd';
import PropTypes from 'prop-types';
import ExpenseExclusionMatches from './ExpenseExclusionMatches';
import { querySourceLog } from '../services/collect';
import {
  formatDataLogDetailValue,
  getDataLogDetailItems,
  getDataLogTypeName,
} from '../utils/data-log-fields';

const stringifyJson = (value) => JSON.stringify(value || {}, null, 2);

const DataLogDetailDrawer = ({ logType, onClose, open, organizationId, record }) => {
  const sourceLogId = record?.sourceLogId;
  const {
    data: sourceLog,
    error,
    isLoading,
  } = useQuery({
    enabled: Boolean(open && organizationId && sourceLogId),
    queryFn: () => querySourceLog({ organizationId, sourceLogId }),
    queryKey: ['settings-source-log', organizationId, sourceLogId],
  });
  const tabName = getDataLogTypeName(logType);
  const sourceItems = sourceLog
    ? [
        { key: 'id', label: '来源日志ID', children: sourceLog.id },
        { key: 'connectionType', label: '连接类型', children: sourceLog.connectionType },
        { key: 'collectionType', label: '采集类型', children: sourceLog.collectionType },
        { key: 'runId', label: '运行ID', children: sourceLog.runId },
        {
          key: 'sourceUpdatedAt',
          label: '来源更新时间',
          children: formatDataLogDetailValue({ dataIndex: 'createdAt' }, sourceLog.sourceUpdatedAt),
        },
        {
          key: 'createdAt',
          label: '采集时间',
          children: formatDataLogDetailValue({ dataIndex: 'createdAt' }, sourceLog.createdAt),
        },
      ]
    : [];

  return (
    <Drawer destroyOnHidden open={open} onClose={onClose} size={760} title={`${tabName}详情`}>
      <Typography.Title level={5}>领域数据</Typography.Title>
      <Descriptions column={1} items={getDataLogDetailItems(logType, record)} />
      {open && logType === 'expense' && organizationId && record?.id && (
        <ExpenseExclusionMatches
          key={`${organizationId}:${record.id}`}
          organizationId={organizationId}
          eventId={record.id}
        />
      )}
      <Divider />
      <Typography.Title level={5}>原始采集快照</Typography.Title>
      {!sourceLogId ? <Alert showIcon type="info" message="当前数据没有关联来源日志" /> : null}
      {isLoading ? <Spin /> : null}
      {error ? <Alert showIcon type="error" message={error.message || '来源日志加载失败'} /> : null}
      {sourceLog ? (
        <>
          <Descriptions column={1} items={sourceItems} />
          <Typography.Title level={5}>来源定位</Typography.Title>
          <Input.TextArea
            readOnly
            autoSize={{ minRows: 3, maxRows: 12 }}
            value={stringifyJson(sourceLog.sourceLocator)}
          />
          <Typography.Title level={5}>原始数据</Typography.Title>
          <Input.TextArea
            readOnly
            autoSize={{ minRows: 6, maxRows: 20 }}
            value={stringifyJson(sourceLog.payload)}
          />
        </>
      ) : null}
    </Drawer>
  );
};

DataLogDetailDrawer.propTypes = {
  logType: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  organizationId: PropTypes.string,
  record: PropTypes.shape({ id: PropTypes.string, sourceLogId: PropTypes.string }),
};

export default DataLogDetailDrawer;
